<template>
  <v-app id="account-holder-app">
    <pc-singleton-modals
      id="singleton-account-holder-modals"
    ></pc-singleton-modals>
    <pc-singleton-overlay
      id="singleton-account-holder-overlay"
    ></pc-singleton-overlay>
    <pc-singleton-snackbar
      id="singleton-account-holder-snackbar"
    ></pc-singleton-snackbar>

    <div v-if="initialAuthenticationRequestComplete" class="fill-height">
      <router-view></router-view>
    </div>
  </v-app>
</template>

<script>
import notifyPendo from "@/shared/lib/notifyPendo";
import NextLevelAccount from "@/shared/lib/client-sdk/models/next-level-account";
import { goToClientPortal } from "@/shared/lib/client-portal-handoff";

export default {
  components: {},

  data: () => ({
    initialAuthenticationRequestComplete: false,
  }),

  methods: {
    logoutAuthenticatedUser() {},

    updateFavicon() {
      const favicon = document.getElementById("favicon");
      const hasCustomBrand =
        this.$store.state.nliraCustomBranding?.effectiveNliraCustomBrand
          ?.displayName;

      if (hasCustomBrand) {
        // Set transparent favicon for custom brands
        favicon.href =
          'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"><rect width="16" height="16" fill="none"/></svg>';
      } else {
        // Use participant favicon for default brands
        favicon.href =
          window.amplifyRuntimeConfig.get("VUE_APP_PARTICIPANT_BASE_URL") +
          "/participant-favicon.ico";
      }
    },
  },

  watch: {
    "$store.state.nliraCustomBranding.effectiveNliraCustomBrand": {
      handler(newValue, oldValue) {
        this.updateFavicon();
      },
      immediate: true,
    },
  },

  mounted: function () {
    if (
      window.amplifyRuntimeConfig.get("VUE_APP_ENVIRONMENT") === "production"
    ) {
      var script = document.createElement("script");
      script.setAttribute(
        "src",
        "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
      );
      script.setAttribute("type", "text/javascript");
      script.setAttribute("charset", "UTF-8");
      script.setAttribute(
        "data-domain-script",
        "0193dbc2-17ed-7302-8f78-e7ab970b571e"
      );
      document.head.appendChild(script);
    }

    var script = document.createElement("script");
    script.setAttribute("src", "https://www.google.com/recaptcha/api.js");
    script.setAttribute("defer", "");
    script.setAttribute("type", "text/javascript");
    script.setAttribute("charset", "UTF-8");
    document.head.appendChild(script);

    // This Account Holder App component is only mounted once per page load, so this is the area to ACCOUNT HOLDER site bootstrapping

    // Initalize with the progress overlay
    var options = {
      subtext: "Loading...",
      opacity: 0.001,
      isParticipantPortalField: true,
    };
    this.$overlay.show(options);

    // Refresh authenticated user.
    this.$store.dispatch(
      "refreshAuthenticatedUser",
      function (user) {
        if (user && user.isStaffMember()) {
          document.location = window.amplifyRuntimeConfig.get(
            "VUE_APP_ACCOUNT_HOLDER_BASE_URL"
          );
        } else if (user && user.isClient()) {
          goToClientPortal(window.amplifyRuntimeConfig);
        } else if (
          user &&
          !user.hasAccessToPortal("nextLevel") &&
          user.hasAccessToPortal("participant")
        ) {
          // Only redirect to the participant portal if the user has participant access.
          document.location = window.amplifyRuntimeConfig.get(
            "VUE_APP_PARTICIPANT_BASE_URL"
          );
        } else {
          // Only set the header when the user has nextLevel access;
          // middleware would reject anonymous visitors and unmigrated users.
          if (user && user.hasAccessToPortal("nextLevel")) {
            window.amplifyActivePortal = "nextLevel";
          }

          this.$overlay.hide();
          this.initialAuthenticationRequestComplete = true;
          notifyPendo(user);

          if (user) {
            NextLevelAccount.syncPersonalization();
          }
        }
      }.bind(this)
    );

    // Lookup data is the same across portals, this runs before X-Active-Portal is set.
    this.$cache.fetchAndCacheLookupData();
  },
};
</script>

<style lang="scss">
#participant-app.v-application {
  font-family: "Roboto", sans-serif;

  body,
  p {
    color: $penChecksDarkGray;
  }

  h1 {
    font-size: 22px;
    font-weight: 400;
    color: $penChecksNavyBlue;
  }

  h2 {
    font-size: 18px;
    font-weight: 400;
    color: $penChecksDarkTeal;
  }

  h3 {
    font-size: 20px;
    font-weight: 500;
    color: $penChecksGreen;
  }

  a {
    color: $penChecksTeal;
    text-decoration: none;
  }

  a.upper {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    color: $penChecksDarkTeal;
  }

  .text-small {
    font-size: 12px;
    color: $penChecksMediumGray;
  }

  .text-medium {
    font-size: 14px;
    color: $penChecksMediumGray;
  }

  .text-question {
    font-size: 18px;
    font-weight: 500;
    color: $penChecksDarkTeal;
    padding-bottom: 0;
  }

  .text-x-large {
    font-size: 36px;
    font-weight: 500;
    color: $penChecksDarkGray;
  }

  .text-x-semi-large {
    font-size: 24px;
    font-weight: 500;
    color: $penChecksDarkGray;
  }

  .founds-caption {
    font-size: 14px !important;
  }

  .text-currency-header {
    font-size: 20px;
    font-weight: 500;
    color: $penChecksDarkGray;
  }

  .text-currency-header-blue {
    font-size: 20px;
    font-weight: 500;
    color: $penChecksDarkTeal;
  }

  .text-currency-medium {
    font-size: 16px;
    font-weight: 500;
  }

  .text-header-medium {
    font-size: 16px;
    font-weight: 500;
    color: $penChecksDarkGray;
  }

  .text-header-blue-medium {
    font-size: 18px;
    font-weight: 500;
    color: $penChecksDarkTeal;
  }

  .text-header-blue16-medium {
    font-size: 16px;
    font-weight: 500;
    color: $penChecksDarkTeal;
  }

  .text-header-regular {
    font-size: 20px;
    font-weight: 400;
    color: $penChecksDarkGray;
  }

  .text-regular-16 {
    font-size: 16px;
    font-weight: 400;
    color: $penChecksDarkGray;
  }

  .text-regular-14 {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: $penChecksDarkGray;
  }

  .text-regular-12 {
    font-size: 12px;
    font-weight: 400;
    color: $penChecksDarkGray;
  }

  .text-concat {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .text-home-image {
    font-size: 20px;
    font-weight: 500;
    margin: 24px;
    color: $penChecksOrange;
  }

  // Vuetify v2.4 changed styling for nested grid items, causing spacing across the
  // app to be affected. A solution was found here that restores our previous spacing:
  // https://github.com/vuetifyjs/vuetify/issues/12848
  .row {
    margin-top: 0;
    margin-bottom: 0;
  }

  .row + .row {
    margin-top: 0;
  }
}
</style>
